/**
 * Task Filters Component
 * TASK-359: TaskListPage & Dashboard Integration
 *
 * Filter controls for the task list.
 */

import React from 'react';
import { Filter, X } from 'lucide-react';
import {
  TaskListFilters,
  TaskType,
  TaskPriority,
  TaskStatus,
  TASK_TYPE_OPTIONS,
  PRIORITY_OPTIONS,
} from '../../types/tasks';

interface TaskFiltersProps {
  filters: TaskListFilters;
  onFiltersChange: (filters: TaskListFilters) => void;
  showClientFilter?: boolean;
}

const TaskFilters: React.FC<TaskFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  // Count active filters
  const activeFilterCount = [
    filters.status,
    filters.task_type,
    filters.priority,
    filters.client_id,
    filters.assignee_user_id,
  ].filter(Boolean).length;

  // Clear all filters
  const clearFilters = () => {
    onFiltersChange({
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
      limit: filters.limit,
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters</span>
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
              {activeFilterCount} active
            </span>
          )}
        </div>
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <X className="w-3 h-3" />
            Clear all
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Status Filter */}
        <div>
          <label htmlFor="task-filter-status" className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select
            id="task-filter-status"
            value={filters.status || ''}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                status: (e.target.value as TaskStatus | 'overdue') || undefined,
              })
            }
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            {/* TASK-684: without this the RM has no way to see what the AI finished and
                left for sign-off. Curated subset on purpose — `cancelled` stays hidden. */}
            <option value="review">Needs Review</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>

        {/* Task Type Filter */}
        <div>
          <label htmlFor="task-filter-type" className="block text-xs font-medium text-gray-500 mb-1">Type</label>
          <select
            id="task-filter-type"
            value={filters.task_type || ''}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                task_type: (e.target.value as TaskType) || undefined,
              })
            }
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All types</option>
            <optgroup label="Follow-ups">
              {TASK_TYPE_OPTIONS.filter((t) => t.category === 'follow_up').map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="Tasks">
              {TASK_TYPE_OPTIONS.filter((t) => t.category === 'task').map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* Priority Filter */}
        <div>
          <label htmlFor="task-filter-priority" className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
          <select
            id="task-filter-priority"
            value={filters.priority || ''}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                priority: (e.target.value as TaskPriority) || undefined,
              })
            }
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All priorities</option>
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Sort By */}
        <div>
          <label htmlFor="task-filter-sort" className="block text-xs font-medium text-gray-500 mb-1">Sort by</label>
          <select
            id="task-filter-sort"
            value={`${filters.sort_by || 'due_date'}_${filters.sort_order || 'asc'}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('_');
              onFiltersChange({
                ...filters,
                sort_by: sortBy as 'due_date' | 'priority' | 'created_at' | 'updated_at',
                sort_order: sortOrder as 'asc' | 'desc',
              });
            }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="due_date_asc">Due date (earliest)</option>
            <option value="due_date_desc">Due date (latest)</option>
            <option value="priority_desc">Priority (highest)</option>
            <option value="priority_asc">Priority (lowest)</option>
            <option value="created_at_desc">Newest first</option>
            <option value="created_at_asc">Oldest first</option>
          </select>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
        <span className="text-xs text-gray-500 mr-2">Quick filters:</span>
        <button
          onClick={() =>
            onFiltersChange({
              ...filters,
              status: 'overdue',
            })
          }
          className={`
            px-2 py-1 text-xs rounded-full transition-colors
            ${filters.status === 'overdue'
              ? 'bg-red-100 text-red-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
          `}
        >
          Overdue
        </button>
        <button
          onClick={() =>
            onFiltersChange({
              ...filters,
              priority: 'high',
            })
          }
          className={`
            px-2 py-1 text-xs rounded-full transition-colors
            ${filters.priority === 'high'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
          `}
        >
          High Priority
        </button>
        <button
          onClick={() =>
            onFiltersChange({
              ...filters,
              priority: 'critical',
            })
          }
          className={`
            px-2 py-1 text-xs rounded-full transition-colors
            ${filters.priority === 'critical'
              ? 'bg-red-100 text-red-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
          `}
        >
          Critical
        </button>
        <button
          onClick={() =>
            onFiltersChange({
              ...filters,
              task_type: 'follow_up_call',
            })
          }
          className={`
            px-2 py-1 text-xs rounded-full transition-colors
            ${filters.task_type === 'follow_up_call'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
          `}
        >
          Follow-up Calls
        </button>
        <button
          onClick={() =>
            onFiltersChange({
              ...filters,
              status: 'completed',
            })
          }
          className={`
            px-2 py-1 text-xs rounded-full transition-colors
            ${filters.status === 'completed'
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
          `}
        >
          Completed
        </button>
      </div>
    </div>
  );
};

export default TaskFilters;
